import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Pages
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import SalesOrders from './pages/SalesOrders';
import CreateSalesOrder from './pages/CreateSalesOrder';
import PurchaseOrders from './pages/PurchaseOrders';
import CreatePurchaseOrder from './pages/CreatePurchaseOrder';
import Inventory from './pages/Inventory';
import Vendors from './pages/Vendors';
import POS from './pages/POS';
import Login from './pages/Login';
import Import from './pages/Import';
import ExportPage from './pages/ExportPage';
import Customers from './pages/Customers';
import BarcodeGenerator from './pages/BarcodeGenerator';
import SalesOrderDetail from './pages/SalesOrderDetail';
import PurchaseOrderDetail from './pages/PurchaseOrderDetail';
import ProductDetail from './pages/ProductDetail';
import InvoicePrint from './pages/InvoicePrint';
import POPrint from './pages/POPrint';
import SalesTaxReport from './pages/SalesTaxReport';
import BulkBarcodePrint from './pages/BulkBarcodePrint';
import LoginHistory from './pages/LoginHistory';
import PricingSchemes from './pages/PricingSchemes';
import ProductCategories from './pages/ProductCategories';
import InventoryReports from './pages/InventoryReports';
import Users from './pages/Users';
import BarcodePrintPage from './pages/BarcodePrintPage';
import Reports from './pages/Reports';
import StockMovements from './pages/StockMovements';
import StockAdjustments from './pages/StockAdjustments';
import ShipmentsReceived from './pages/ShipmentsReceived';
import SalesReturns from './pages/SalesReturns';
import SalesReturnDetail from './pages/SalesReturnDetail';
import ShipmentReturns from './pages/ShipmentReturns';
import AccountStatements from './pages/AccountStatements';
import AccountStatementDetail from './pages/AccountStatementDetail';
import PaymentsReceived from './pages/PaymentsReceived';
import StockCount from './pages/StockCount';
import PurchaseQuotes from './pages/PurchaseQuotes';
import SalesQuotes from './pages/SalesQuotes';
// PAUSED: Online Store (re-enable later)
// import StoreApp from './pages/store/StoreApp';

// Layout
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin-only Route: staff who type an admin URL are sent back to the dashboard.
// (Backend endpoints are admin-protected too; this keeps the UI consistent with the hidden nav.)
const AdminRoute = ({ children }) => {
  let role = null;
  try { role = JSON.parse(localStorage.getItem('user') || 'null')?.role; } catch { role = null; }
  if (role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (_e) {
        localStorage.removeItem('user');
      }
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try { await (await import('./api')).default.logout(); } catch {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        
        {/* PAUSED: Online Store - re-enable later */}
        {/* <Route path="/store/*" element={<StoreApp />} /> */}
        
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Print pages - outside Layout (no sidebar) */}
        <Route path="/sales-orders/:id/invoice" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
        <Route path="/purchase-orders/:id/print" element={<ProtectedRoute><POPrint /></ProtectedRoute>} />
        
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/sales-orders" element={<SalesOrders />} />
                  <Route path="/sales-orders/new" element={<CreateSalesOrder />} />
                  <Route path="/sales-orders/:id" element={<SalesOrderDetail />} />
                  <Route path="/purchase-orders" element={<PurchaseOrders />} />
                  <Route path="/purchase-orders/new" element={<CreatePurchaseOrder />} />
                  <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/vendors" element={<Vendors />} />
                  <Route path="/pos" element={<POS />} />
                  <Route path="/import" element={<AdminRoute><Import /></AdminRoute>} />
                  <Route path="/export" element={<AdminRoute><ExportPage /></AdminRoute>} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/barcode-generator" element={<BarcodeGenerator />} />
                  <Route path="/barcode-print" element={<BulkBarcodePrint />} />
                  <Route path="/reports/sales-tax" element={<SalesTaxReport />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/stock-movements" element={<StockMovements />} />
                  <Route path="/stock-adjustments" element={<StockAdjustments />} />
                  <Route path="/shipments-received" element={<ShipmentsReceived />} />
                  <Route path="/account-statements" element={<AccountStatements />} />
                  <Route path="/account-statements/:customerName" element={<AccountStatementDetail />} />
                  <Route path="/payments-received" element={<PaymentsReceived />} />
                  <Route path="/stock-count" element={<StockCount />} />
                  <Route path="/purchase-quotes" element={<PurchaseQuotes />} />
                  <Route path="/sales-quotes" element={<SalesQuotes />} />
                  <Route path="/sales-returns" element={<SalesReturns />} />
                  <Route path="/sales-returns/:id" element={<SalesReturnDetail />} />
                  <Route path="/shipment-returns" element={<ShipmentReturns />} />
                  <Route path="/admin/login-history" element={<AdminRoute><LoginHistory /></AdminRoute>} />
                  <Route path="/admin/pricing-schemes" element={<AdminRoute><PricingSchemes /></AdminRoute>} />
                  <Route path="/product-categories" element={<ProductCategories />} />
                  <Route path="/inventory-reports" element={<InventoryReports />} />
                  <Route path="/admin/users" element={<AdminRoute><Users /></AdminRoute>} />
                  <Route path="/print-barcode" element={<BarcodePrintPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
